import { COLLISION_CELLS_PER_TILE, MAX_TILESET_INDEX, frameId, objectAnchor, packOf, propFeet, roleLayers, type CompileContext, type EntityInstance, type LayerDocument, type MapDocument, type PropBehaviour, type PropFootprint, type PropInstance, type TileRef, type ValidationIssue, type WorldDocument } from "daedalus-core";
import { SIXTEENTHS } from "../movement/step.ts";
import { shrineGround } from "../shrines/ground.ts";
import { shrineActivationCells } from "../shrines/reach.ts";
import { isShrineLoopName, shrinePopName, type NamedFrame } from "./shrine-pair.ts";
import { SHRINE } from "./type-names.ts";

/**
 * Carries a shrine saved as a point marker over to a behaviour on an obelisk (ADR-096 decision 43 and its amendment
 * of 2026-10-07). No marker type is registered: a shrine is a Shrine prop and nothing else, and this step is what
 * turns a saved marker into one. It is pure, so every reader of a document applies it in memory, as the well's
 * upgrade is applied:
 *
 * - a marker with a "buff available" obelisk whose ground point (the stone's base, not the frame's bottom) lies
 *   within 3 tiles of it: the nearest such prop gains the Shrine behaviour with the marker's fields exactly as they
 *   are (`name`, `charges`, `lit`, `start` and anything else it holds), `dormant` on the last frame of the paired pop
 *   strip, that strip named `pop`, and the shrine's reach as its activation cells; the marker is removed;
 * - a marker with no such prop: an obelisk carrying the same behaviour is placed with its ground point on the
 *   marker's point, under the marker's id, in the prop layer that sorts with characters; the marker is removed;
 * - a marker for which no obelisk can be placed, or whose id a prop already holds: the marker is removed, and the
 *   report says why.
 *
 * One marker takes one prop, in document order. A prop that already carries a behaviour is never taken, and no prop
 * already placed has its art changed. A read handed no frames can neither pair nor place and leaves every marker as
 * it is. A second run changes nothing.
 */

/**
 * How far from a marker an obelisk's ground point may lie to be its shrine, in tiles.
 */
export const SHRINE_PAIR_TILES = 3;

/**
 * The entity types a shrine was saved under as a marker: `shrine` before the shrine was an object type, and
 * `shrine_marker` once an earlier build found no obelisk beside one. Neither is a registered type; they are what this
 * step reads.
 */
const SAVED_MARKER_TYPES: ReadonlySet<string> = new Set([SHRINE, "shrine_marker"]);

/**
 * Why a marker was removed with no shrine in its place: the frames name no usable obelisk of a pack the document
 * lists, the map has no prop layer, the obelisk would stand outside the map, the document's tileset list is full, or a
 * prop already holds the marker's id, so it was carried before.
 */
export type ShrineRemoval = "no obelisk" | "no prop layer" | "outside the map" | "tileset list full" | "carried";

/**
 * One carried shrine, for the report: the marker it was and the prop that is now the shrine. `placed` is the
 * marker's tile when the step placed the obelisk itself; `removed` is the marker's tile and the reason when the marker
 * went with no shrine made, `propId` then naming the prop that holds its id when the reason is `carried`.
 */
export interface ShrineReport {
  readonly mapId: string;
  readonly entityLayerId: string;
  readonly entityId: string;
  readonly name: string;
  readonly propLayerId?: string;
  readonly propId?: string;
  readonly placed?: { readonly x: number; readonly y: number };
  readonly removed?: { readonly x: number; readonly y: number; readonly reason: ShrineRemoval };
}

/**
 * A shrine to stand on an obelisk: the id its prop takes, its ground point in tiles and its fields.
 */
export interface ShrineAt {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly fields: PropBehaviour["fields"];
}

/**
 * What a frame gives a shrine placed on it: the frame its `dormant` state rests on, its pop strip, its activation
 * cells and the footprint its ground point is read from.
 */
interface ObeliskFrame {
  readonly dormant: TileRef;
  readonly pop: TileRef;
  readonly activation: readonly (readonly [number, number])[];
  readonly footprint: PropFootprint;
}

/**
 * An obelisk a marker may take: where it is in the document, the behaviour it would gain but for the fields, and its
 * ground point in tiles.
 */
interface Obelisk extends ObeliskFrame {
  readonly layerId: string;
  readonly prop: PropInstance;
  readonly x: number;
  readonly y: number;
}

/**
 * What the step reads of a world: its strips, footprints and frame sizes, and the catalogue's frames in their order,
 * by id and by tileset.
 */
interface Reading {
  readonly context: Pick<CompileContext, "transitionStrips" | "frameSizes" | "collision">;
  readonly frames: readonly NamedFrame[];
  readonly nameOf: ReadonlyMap<string, string>;
  readonly byTileset: ReadonlyMap<string, readonly NamedFrame[]>;
  readonly tileSize: number;
}

/**
 * Gathers what the step reads.
 *
 * @param context The world's compile context.
 * @param frames The catalogue's prop frames with their names.
 * @param tileSize The world's tile size in pixels.
 * @returns The reading.
 */
function readingOf(context: Reading["context"], frames: readonly NamedFrame[], tileSize: number): Reading {
  const byTileset = new Map<string, NamedFrame[]>();
  for (const frame of frames) {
    const listed = byTileset.get(frame.tileset);
    if (listed === undefined) {
      byTileset.set(frame.tileset, [frame]);
    } else {
      listed.push(frame);
    }
  }
  return { context, frames, nameOf: new Map(frames.map((frame) => [frameId(frame.tileset, null, frame.tile), frame.name])), byTileset, tileSize };
}

/**
 * The footprint a frame's ground point is read from: its own, or, for a frame the game's policy gives none, one of
 * its size that blocks nothing, so the ground point is the feet point.
 *
 * @param reading The world's context.
 * @param id The frame's id.
 * @returns The footprint, or undefined for a frame of unknown size.
 */
function footprintOf(reading: Reading, id: string): PropFootprint | undefined {
  const own = reading.context.collision.footprints?.[id];
  const size = reading.context.frameSizes?.[id];
  return own ?? (size === undefined ? undefined : { w: size.w, h: size.h, base: size.h - 1, runs: [] });
}

/**
 * A frame as an obelisk's, or undefined when it is none: its name is no shrine's loop, its tileset lists no pop strip
 * for it, the world holds no such strip, or its size is unknown.
 *
 * @param reading The world's context and names.
 * @param frame The frame.
 * @returns What a shrine placed on it takes from it.
 */
function obeliskFrame(reading: Reading, frame: TileRef): ObeliskFrame | undefined {
  const id = frameId(frame.tileset, null, frame.tile);
  const name = reading.nameOf.get(id);
  if (name === undefined || !isShrineLoopName(name)) {
    return undefined;
  }
  const listed = reading.byTileset.get(frame.tileset) ?? [];
  const popName = shrinePopName(name, listed.map((candidate) => candidate.name));
  const first = listed.find((candidate) => candidate.name === popName);
  const last = first === undefined ? undefined : reading.context.transitionStrips?.[frameId(first.tileset, null, first.tile)]?.frames.at(-1);
  const footprint = footprintOf(reading, id);
  if (first === undefined || last === undefined || footprint === undefined) {
    return undefined;
  }
  return { dormant: { tileset: last.tileset, tile: last.tile }, pop: { tileset: first.tileset, tile: first.tile }, activation: shrineActivationCells(footprint, reading.tileSize), footprint };
}

/**
 * The Shrine behaviour a prop on an obelisk's frame carries.
 *
 * @param frame What the frame gives.
 * @param fields The shrine's fields, kept as the object they are.
 * @returns The behaviour.
 */
function shrineBehaviour(frame: ObeliskFrame, fields: PropBehaviour["fields"]): PropBehaviour {
  return { type: SHRINE, fields, frames: { dormant: frame.dormant }, animations: { pop: frame.pop }, activation: frame.activation };
}

/**
 * A prop's ground point in tiles, as a shrine on it would stand.
 *
 * @param prop Where the prop is and how it is mirrored.
 * @param footprint The footprint of its frame.
 * @param tileSize Tile size in pixels.
 * @returns The point.
 */
function groundOf(prop: Pick<PropInstance, "x" | "y" | "flipX" | "flipY">, footprint: PropFootprint, tileSize: number): { x: number; y: number } {
  const feet = propFeet(prop, footprint.w, tileSize);
  const ground = shrineGround({ x: feet.x * SIXTEENTHS, y: feet.y * SIXTEENTHS, footprint, flipX: prop.flipX, flipY: prop.flipY }, tileSize);
  return { x: ground.x / SIXTEENTHS, y: ground.y / SIXTEENTHS };
}

/**
 * A prop as an obelisk a marker may take, or undefined when it is none: it carries a behaviour or its frame is no
 * obelisk's.
 *
 * @param reading The world's context and names.
 * @param layerId The prop's layer.
 * @param prop The prop.
 * @returns The obelisk.
 */
function obeliskOf(reading: Reading, layerId: string, prop: PropInstance): Obelisk | undefined {
  const frame = prop.behaviour === undefined ? obeliskFrame(reading, prop.frame) : undefined;
  if (frame === undefined) {
    return undefined;
  }
  return { ...frame, layerId, prop, ...groundOf(prop, frame.footprint, reading.tileSize) };
}

/**
 * The nearest obelisk to a marker that no marker has taken, within `SHRINE_PAIR_TILES` of it; the first in document
 * order when two are as near.
 *
 * @param obelisks The map's obelisks.
 * @param taken The props already taken, by id.
 * @param marker The marker.
 * @returns The obelisk, or undefined when none is near.
 */
function nearestObelisk(obelisks: readonly Obelisk[], taken: ReadonlyMap<string, PropInstance>, marker: EntityInstance): Obelisk | undefined {
  let best: Obelisk | undefined;
  let bestDistance = SHRINE_PAIR_TILES * SHRINE_PAIR_TILES;
  for (const obelisk of obelisks) {
    const distance = (obelisk.x - marker.x) * (obelisk.x - marker.x) + (obelisk.y - marker.y) * (obelisk.y - marker.y);
    if (!taken.has(obelisk.prop.id) && (best === undefined ? distance <= bestDistance : distance < bestDistance)) {
      best = obelisk;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * A position in tiles moved onto the nearest collision cell, where the editor's snap stands a prop.
 *
 * @param value The position in tiles.
 * @param tileSize Tile size in pixels.
 * @returns The position, a whole number of collision cells.
 */
function onCell(value: number, tileSize: number): number {
  const cell = tileSize / COLLISION_CELLS_PER_TILE;
  return (Math.round((value * tileSize) / cell) * cell) / tileSize;
}

/**
 * `shrineObeliskAt` over a reading already gathered.
 *
 * @param reading The world's context and names.
 * @param world The document's tileset list.
 * @param map The size of the map, in tiles.
 * @param shrine The shrine.
 * @returns The prop and the tileset to list, or why none can stand.
 */
function obeliskAt(reading: Reading, world: Pick<WorldDocument, "tilesets">, map: Pick<MapDocument, "width" | "height">, shrine: ShrineAt): { readonly prop: PropInstance; readonly tileset: string | null } | Exclude<ShrineRemoval, "no prop layer" | "carried"> {
  const listed = new Set(world.tilesets.map((tileset) => tileset.id));
  const packs = new Set([...listed].map((id) => packOf(id)));
  let chosen: { readonly loop: NamedFrame; readonly frame: ObeliskFrame } | undefined;
  for (const loop of reading.frames) {
    const pack = packOf(loop.tileset);
    if (!listed.has(loop.tileset) && (pack === null || !packs.has(pack))) {
      continue;
    }
    const frame = obeliskFrame(reading, loop);
    if (frame !== undefined && frame.pop.tileset === loop.tileset && frame.dormant.tileset === loop.tileset) {
      chosen = { loop, frame };
      break;
    }
  }
  if (chosen === undefined) {
    return "no obelisk";
  }
  const tileset = listed.has(chosen.loop.tileset) ? null : chosen.loop.tileset;
  if (tileset !== null && listed.size > MAX_TILESET_INDEX) {
    return "tileset list full";
  }
  const footprint = chosen.frame.footprint;
  const upright = { flipX: false, flipY: false };
  const first = objectAnchor(shrine, footprint.w, reading.tileSize);
  const offset = groundOf({ x: first.x, y: first.y, ...upright }, footprint, reading.tileSize);
  const x = onCell(first.x + shrine.x - offset.x, reading.tileSize);
  const y = onCell(first.y + shrine.y - offset.y, reading.tileSize);
  const feet = propFeet({ x, y }, footprint.w, reading.tileSize);
  if (x < 0 || y < 0 || x >= map.width || y >= map.height || feet.x < 0 || feet.y < 0 || feet.x >= map.width || feet.y >= map.height) {
    return "outside the map";
  }
  return { prop: { id: shrine.id, frame: { tileset: chosen.loop.tileset, tile: chosen.loop.tile }, x, y, ...upright, sortPixels: 0, behaviour: shrineBehaviour(chosen.frame, shrine.fields) }, tileset };
}

/**
 * An obelisk carrying the Shrine behaviour, standing so that its ground point is a point: the placing every reader
 * and every tool of ours shares (ADR-096's amendment of 2026-10-07, decisions 2 and 9). The frame is the first
 * "buff available" loop the frames name that is of a tileset, or of a pack, the document lists, whose pop strip is of
 * its own tileset and whose strip and size the context holds. The prop stands on the collision cells, so its ground
 * point lies within a cell of the point asked for.
 *
 * @param world The document's tile size and tileset list.
 * @param map The size of the map the shrine stands on, in tiles.
 * @param context The world's compile context: its one-shot strips, footprints and frame sizes.
 * @param frames The catalogue's prop frames with their names.
 * @param shrine The shrine: the id its prop takes, its ground point in tiles and its fields, kept as the object
 *   they are.
 * @returns The prop and the tileset the document must add to its list for it (null when it is listed), or why none
 *   can stand: `no obelisk` when no frame serves, `tileset list full` when its tileset cannot be listed, and
 *   `outside the map` when the prop or its feet point would lie off the map.
 */
export function shrineObeliskAt(world: Pick<WorldDocument, "tileSize" | "tilesets">, map: Pick<MapDocument, "width" | "height">, context: Pick<CompileContext, "transitionStrips" | "frameSizes" | "collision">, frames: readonly NamedFrame[], shrine: ShrineAt): { readonly prop: PropInstance; readonly tileset: string | null } | Exclude<ShrineRemoval, "no prop layer" | "carried"> {
  return obeliskAt(readingOf(context, frames, world.tileSize), world, map, shrine);
}

/**
 * The prop layer an obelisk is placed on: the one that sorts with characters, the lowest prop layer of the map's
 * Players band (`roleLayers(map).with`), so a tall prop is not laid among the ground decals under them; the first
 * prop layer in document order on a map whose band holds none.
 *
 * @param map The map.
 * @returns The layer, or undefined for a map with no prop layer.
 */
function placingLayer(map: MapDocument): Extract<LayerDocument, { readonly kind: "prop" }> | undefined {
  const sorts = roleLayers(map).with;
  const props = map.layers.flatMap((layer) => (layer.kind === "prop" ? [layer] : []));
  return props.find((layer) => layer.id === sorts) ?? props[0];
}

/**
 * One map with its shrine markers carried over.
 *
 * @param map The map.
 * @param reading The world's context and names.
 * @param tilesets The document's tileset list, to which a placed obelisk's tileset is appended when it is missing.
 * @param reports Where each carried shrine is recorded.
 * @returns The map, the same object when it holds no marker to carry.
 */
function carriedMap(map: MapDocument, reading: Reading, tilesets: { id: string }[], reports: ShrineReport[]): MapDocument {
  const markers = map.layers.flatMap((layer) => (layer.kind === "entity" ? layer.entities.filter((entity) => SAVED_MARKER_TYPES.has(entity.type)).map((entity) => ({ layerId: layer.id, entity })) : []));
  if (markers.length === 0) {
    return map;
  }
  const obelisks = map.layers.flatMap((layer) => (layer.kind === "prop" ? layer.props.flatMap((prop) => obeliskOf(reading, layer.id, prop) ?? []) : []));
  const held = new Map(map.layers.flatMap((layer) => (layer.kind === "prop" ? layer.props.map((prop): [string, string] => [prop.id, layer.id]) : [])));
  const propLayer = placingLayer(map);
  const shrines = new Map<string, PropInstance>();
  const placed: PropInstance[] = [];
  for (const { layerId, entity } of markers) {
    const name = typeof entity.fields["name"] === "string" ? entity.fields["name"] : "";
    const report = { mapId: map.id, entityLayerId: layerId, entityId: entity.id, name };
    const at = { x: entity.x, y: entity.y };
    const holder = held.get(entity.id);
    if (holder !== undefined) {
      reports.push({ ...report, propLayerId: holder, propId: entity.id, removed: { ...at, reason: "carried" } });
      continue;
    }
    const obelisk = nearestObelisk(obelisks, shrines, entity);
    if (obelisk !== undefined) {
      const { footprint: _own, ...plain } = obelisk.prop;
      shrines.set(obelisk.prop.id, { ...plain, behaviour: shrineBehaviour(obelisk, entity.fields) });
      reports.push({ ...report, propLayerId: obelisk.layerId, propId: obelisk.prop.id });
      continue;
    }
    const made = propLayer === undefined ? "no prop layer" : obeliskAt(reading, { tilesets }, map, { id: entity.id, ...at, fields: entity.fields });
    if (typeof made === "string" || propLayer === undefined) {
      reports.push({ ...report, removed: { ...at, reason: typeof made === "string" ? made : "no prop layer" } });
      continue;
    }
    if (made.tileset !== null) {
      tilesets.push({ id: made.tileset });
    }
    placed.push(made.prop);
    held.set(entity.id, propLayer.id);
    reports.push({ ...report, propLayerId: propLayer.id, propId: entity.id, placed: at });
  }
  const layers = map.layers.map((layer): LayerDocument => {
    if (layer.kind === "prop" && (layer.props.some((prop) => shrines.has(prop.id)) || (layer === propLayer && placed.length > 0))) {
      return { ...layer, props: [...layer.props.map((prop) => shrines.get(prop.id) ?? prop), ...(layer === propLayer ? placed : [])] };
    }
    if (layer.kind === "entity" && layer.entities.some((entity) => SAVED_MARKER_TYPES.has(entity.type))) {
      return { ...layer, entities: layer.entities.filter((entity) => !SAVED_MARKER_TYPES.has(entity.type)) };
    }
    return layer;
  });
  return { ...map, layers };
}

/**
 * Carries every shrine marker of a world over.
 *
 * @param world The document.
 * @param context The world's compile context: its one-shot strips, footprints and frame sizes.
 * @param frames The catalogue's prop frames with their names, which pairing and placing read; with an empty list
 *   nothing can be paired or placed, and every marker is left as it is.
 * @returns The document, the same object when no marker was carried, and one report per carried marker in document
 *   order.
 */
export function shrineMarkers(world: WorldDocument, context: Pick<CompileContext, "transitionStrips" | "frameSizes" | "collision">, frames: readonly NamedFrame[]): { readonly world: WorldDocument; readonly shrines: readonly ShrineReport[] } {
  const shrines: ShrineReport[] = [];
  if (frames.length === 0) {
    return { world, shrines };
  }
  const reading = readingOf(context, frames, world.tileSize);
  const tilesets = world.tilesets.map((tileset) => ({ id: tileset.id }));
  const maps = world.maps.map((map) => carriedMap(map, reading, tilesets, shrines));
  if (shrines.length === 0) {
    return { world, shrines };
  }
  return { world: { ...world, ...(tilesets.length === world.tilesets.length ? {} : { tilesets: [...world.tilesets, ...tilesets.slice(world.tilesets.length)] }), maps }, shrines };
}

/**
 * What to do by hand where a marker went and no shrine was made.
 *
 * @param removed The marker's tile and why no obelisk was placed.
 * @returns The words.
 */
function byHand(removed: NonNullable<ShrineReport["removed"]>): string {
  return `since the marker type no longer exists and no obelisk could be placed for it (${removed.reason}); to have a shrine here, place a 'shrine-buff available' prop at tile ${removed.x}, ${removed.y} and switch Shrine on`;
}

/**
 * One line for a carried shrine, for a compile's warnings and `world:migrate`.
 *
 * @param report The report.
 * @returns The line.
 */
export function describeShrine(report: ShrineReport): string {
  const head = `shrine ${report.entityId} ${JSON.stringify(report.name)} (map ${report.mapId}, layer ${report.entityLayerId})`;
  if (report.removed !== undefined) {
    return report.removed.reason === "carried" ? `${head}: its marker is removed, since prop ${report.propId} already holds its id` : `${head}: removed, ${byHand(report.removed)}`;
  }
  if (report.placed !== undefined) {
    return `${head}: now a Shrine on an obelisk placed at tile ${report.placed.x}, ${report.placed.y} (prop ${report.propId}, layer ${report.propLayerId}), where its marker was, with the marker's fields; move or restyle it as you like`;
  }
  return `${head}: now a Shrine on prop ${report.propId} (layer ${report.propLayerId}), with the marker's fields; the marker is removed`;
}

/**
 * The editor's notes on the shrines a map's document was upgraded with as it opened, each saying a save keeps it: a
 * warning on each prop that became a shrine, on each obelisk the read placed, and on the marker layer of each marker
 * removed with no shrine made. A marker removed because a prop already held its id is not noted, since nothing a
 * builder placed changed. A prop or a layer no longer on the map is not noted.
 *
 * @param shrines The reports of the upgrade.
 * @param map The map in view.
 * @returns The warnings, in report order.
 */
export function shrineNotes(shrines: readonly ShrineReport[], map: MapDocument): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const shrine of shrines) {
    if (shrine.mapId !== map.id) {
      continue;
    }
    if (shrine.removed !== undefined) {
      if (shrine.removed.reason !== "carried" && map.layers.some((candidate) => candidate.id === shrine.entityLayerId && candidate.kind === "entity")) {
        issues.push({ mapId: shrine.mapId, kind: "shrine.upgraded", layerId: shrine.entityLayerId, severity: "warning", message: `the shrine marker ${shrine.entityId} ${JSON.stringify(shrine.name)} was removed as the world was opened, ${byHand(shrine.removed)}; save to keep it` });
      }
      continue;
    }
    const layer = map.layers.find((candidate) => candidate.id === shrine.propLayerId);
    if (shrine.propId === undefined || shrine.propLayerId === undefined || layer?.kind !== "prop" || !layer.props.some((prop) => prop.id === shrine.propId)) {
      continue;
    }
    const message =
      shrine.placed === undefined
        ? `prop ${shrine.propId} is a Shrine made from the marker ${shrine.entityId} as the world was opened: it carries the marker's name, charges and lit, and the marker is gone; save to keep it`
        : `prop ${shrine.propId} is a Shrine on an obelisk placed at tile ${shrine.placed.x}, ${shrine.placed.y} as the world was opened, where the marker ${shrine.entityId} was: it carries the marker's name, charges and lit, and the marker is gone; move or restyle it as you like, and save to keep it`;
    issues.push({ mapId: shrine.mapId, kind: "shrine.upgraded", layerId: shrine.propLayerId, propId: shrine.propId, severity: "warning", message });
  }
  return issues;
}
